/* erx-header.js - behaviour for snippets/ergonix-header.liquid (desktop mega panel, phone menu, cart button).
   Figma 2054:378 builder notes:
   - desktop: the panel opens after ~150 ms of hover on any menu item, closes when the pointer leaves or on Esc,
     the page under it dims 28 %. A click on a menu item goes straight to that page.
   - phone and tablet (<= 1024 px): the menu button opens a full-screen menu, the icon turns into a cross that
     closes it, the black bar stays on top, the page under it does not scroll.
   Safe to load more than once (some templates render the header twice): every root initialises once. */
(function () {
  'use strict';
  var OPEN_DELAY = 150;
  var CLOSE_DELAY = 120;
  var uid = 0;

  function openCart(e) {
    if (window.cart && typeof window.cart.openDrawer === 'function') {
      e.preventDefault();
      try { window.cart.openDrawer(); } catch (err) { window.location.href = e.currentTarget.getAttribute('href') || '/cart'; }
    }
  }

  function initDesktop(root) {
    var wrap = root.querySelector('[data-erx-barwrap]');
    var panel = root.querySelector('[data-erx-panel]');
    var dim = root.querySelector('[data-erx-dim]');
    var links = root.querySelectorAll('[data-erx-nav]');
    if (!wrap || !panel || !links.length) return;

    var openT = 0, closeT = 0, isOpen = false, active = null, quietFocus = false;
    var panelId = 'erx-hd-panel-' + (++uid);
    panel.id = panelId;

    function canHover() { return window.matchMedia('(hover: hover) and (min-width: 1025px)').matches; }
    var ind = root.querySelector('[data-erx-ind]');
    var groups = panel.querySelectorAll('[data-erx-group]');
    function moveInd(link) {
      if (!ind) return;
      if (!link) { ind.classList.remove('is-on'); return; }
      var text = link.querySelector('.erx-hd__nav-text') || link;
      var navBox = ind.parentNode.getBoundingClientRect(), box = text.getBoundingClientRect();
      var wasOn = ind.classList.contains('is-on');
      // slide only between items; the first appearance fades in where it lands
      ind.classList.toggle('is-sliding', wasOn);
      ind.style.width = box.width + 'px';
      ind.style.transform = 'translateX(' + (box.left - navBox.left) + 'px)';
      if (!wasOn) { void ind.offsetWidth; ind.classList.add('is-on'); }
    }
    function showGroup(link) {
      if (!link || !groups.length) return;
      var i = Array.prototype.indexOf.call(links, link);
      groups.forEach(function (g) { g.classList.toggle('is-on', g.getAttribute('data-erx-group') === String(i)); });
    }
    function setActive(link) {
      if (active === link) return;
      if (active) active.classList.remove('is-active');
      active = link;
      if (active) active.classList.add('is-active');
      moveInd(active);
      showGroup(active);
    }
    function mount() {
      panel.classList.add('is-mounted');
      if (dim) dim.classList.add('is-mounted');
    }
    /* the pointer crossed a menu item and left before the open delay ran out: take the still invisible panel and
       full-screen dim back out, or they stay over the page and swallow every click until a reload (2026-10-14) */
    function unmount() {
      if (isOpen) return;
      panel.classList.remove('is-mounted');
      if (dim) dim.classList.remove('is-mounted');
    }
    function open() {
      clearTimeout(closeT);
      if (isOpen) return;
      isOpen = true;
      mount();
      // next frame so the fade runs from the mounted, transparent state
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (!isOpen) return;
          panel.classList.add('is-open');
          if (dim) dim.classList.add('is-open');
        });
      });
      links.forEach(function (l) { l.setAttribute('aria-expanded', 'true'); });
      root.classList.add('is-panel-open');
    }
    function close() {
      clearTimeout(openT);
      if (!isOpen) { unmount(); return; }
      isOpen = false;
      panel.classList.remove('is-open');
      if (dim) dim.classList.remove('is-open');
      setActive(null);
      links.forEach(function (l) { l.setAttribute('aria-expanded', 'false'); });
      root.classList.remove('is-panel-open');
      setTimeout(function () {
        if (isOpen) return;
        panel.classList.remove('is-mounted');
        if (dim) dim.classList.remove('is-mounted');
      }, 180);
    }
    function scheduleClose() {
      clearTimeout(openT);
      clearTimeout(closeT);
      closeT = setTimeout(close, CLOSE_DELAY);
    }

    links.forEach(function (link) {
      link.setAttribute('aria-controls', panelId);
      link.setAttribute('aria-expanded', 'false');
      link.addEventListener('mouseenter', function () {
        if (!canHover()) return;
        clearTimeout(closeT);
        mount(); // starts the lazy images loading during the delay
        if (!isOpen) showGroup(link);
        if (isOpen) { setActive(link); return; }
        clearTimeout(openT);
        openT = setTimeout(function () { setActive(link); open(); }, OPEN_DELAY);
      });
      link.addEventListener('focus', function () {
        if (quietFocus || window.innerWidth < 1025) return;
        setActive(link);
        open();
      });
    });
    // leaving the bar or the panel closes; moving between them does not
    wrap.addEventListener('mouseleave', function () { if (canHover()) scheduleClose(); });
    wrap.addEventListener('mouseenter', function () { if (isOpen) clearTimeout(closeT); });
    // leaving the nav towards the logo or the cart before the delay ran out cancels the open
    var nav = root.querySelector('.erx-hd__nav');
    if (nav) nav.addEventListener('mouseleave', function () { if (!isOpen) { clearTimeout(openT); unmount(); } });
    if (dim) dim.addEventListener('click', close);
    root.addEventListener('focusout', function (e) {
      if (!wrap.contains(e.relatedTarget)) close();
    });
    document.addEventListener('keydown', function (e) {
      if ((e.key === 'Escape' || e.key === 'Esc') && isOpen) {
        // focus was inside the panel: hand it back to the menu item without reopening the panel
        var back = panel.contains(document.activeElement) ? active : null;
        close();
        if (back) { quietFocus = true; back.focus({ preventScroll: true }); quietFocus = false; }
      }
    });
    window.addEventListener('resize', function () { if (window.innerWidth < 1025) close(); });
  }

  function initPhone(root) {
    var bars = root.querySelector('[data-erx-hp]');
    var btn = root.querySelector('[data-erx-menu-toggle]');
    var menu = root.querySelector('[data-erx-mm]');
    if (!bars || !btn || !menu) return;
    var menuId = 'erx-mm-' + (++uid);
    menu.id = menuId;
    btn.setAttribute('aria-controls', menuId);

    var labelOpen = btn.getAttribute('data-label-open') || '';
    var labelClose = btn.getAttribute('data-label-close') || '';
    var lockedY = 0, isOpen = false;
    var body = document.body;
    var saved = null;

    function lock() {
      lockedY = window.scrollY || window.pageYOffset || 0;
      saved = { position: body.style.position, top: body.style.top, left: body.style.left, right: body.style.right, width: body.style.width, overflow: body.style.overflow };
      body.style.position = 'fixed';
      body.style.top = (-lockedY) + 'px';
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
    }
    function unlock() {
      if (!saved) return;
      body.style.position = saved.position; body.style.top = saved.top; body.style.left = saved.left;
      body.style.right = saved.right; body.style.width = saved.width; body.style.overflow = saved.overflow;
      saved = null;
      var html = document.documentElement, prev = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto'; // ergonix-reset.css sets smooth scrolling globally
      window.scrollTo(0, lockedY);
      html.style.scrollBehavior = prev;
      /* the jump back from 0 to lockedY is not a scroll down: do not hide the bars for it */
      if (root._erxSyncScroll) root._erxSyncScroll();
    }
    function open() {
      if (isOpen) return;
      isOpen = true;
      if (root._erxShowBars) root._erxShowBars();
            root.classList.add('is-menu-open');
      clearTimeout(menu.__erxOut);
      menu.removeAttribute('hidden');
      menu.setAttribute('aria-hidden', 'false');
      void menu.offsetWidth; menu.classList.add('is-in'); /* rolls down (CSS) */
      btn.setAttribute('aria-expanded', 'true');
      if (labelClose) btn.setAttribute('aria-label', labelClose);
      lock();
    }
    function close(skipFocus) {
      if (!isOpen) return;
      isOpen = false;
      /* roll back up first, then take it out of the page */
      menu.classList.remove('is-in');
      var still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      clearTimeout(menu.__erxOut);
      menu.__erxOut = setTimeout(function () { if (isOpen) return; root.classList.remove('is-menu-open'); menu.setAttribute('hidden', ''); }, still ? 0 : 380);
      menu.setAttribute('aria-hidden', 'true');
      btn.setAttribute('aria-expanded', 'false');
      if (labelOpen) btn.setAttribute('aria-label', labelOpen);
      unlock();
      if (!skipFocus) btn.focus({ preventScroll: true });
    }
    btn.addEventListener('click', function (e) { e.preventDefault(); isOpen ? close() : open(); });
    document.addEventListener('keydown', function (e) { if ((e.key === 'Escape' || e.key === 'Esc') && isOpen) close(); });
    // a link inside the menu: let it navigate, but unlock first so a same-page anchor lands correctly
    menu.addEventListener('click', function (e) { if (e.target.closest('a')) close(true); });
    window.addEventListener('resize', function () { if (window.innerWidth >= 1025 && isOpen) close(true); });
    root._erxCloseMenu = function () { close(true); };

    /* Hide-on-scroll (owner 2026-10-12, Figma 2139:462): on a phone both top bars slide away while the visitor
       scrolls DOWN and come back together on the first move UP. 82 of 664 px go back to the content.
       html.erx-bars-hidden tells the product pages' tab bar to stick at top: 0 instead of 82px.
       Never hidden: near the top of the page, with the menu open, while focus is inside the bars, >= 1025 px. */
    var html = document.documentElement, barsHidden = false, lastY = Math.max(0, window.scrollY || 0), run = 0, raf = 0;
    function setBars(hide) {
      if (hide === barsHidden) return;
      barsHidden = hide;
      root.classList.toggle('is-bars-hidden', hide);
      html.classList.toggle('erx-bars-hidden', hide);
    }
    function onScroll() {
      raf = 0;
      var y = Math.max(0, window.scrollY || window.pageYOffset || 0), dy = y - lastY;
      lastY = y;
      if (window.innerWidth >= 1025 || isOpen || saved) { run = 0; setBars(false); return; }
      if (y < 120) { run = 0; setBars(false); return; }
      /* iOS rubber band at the very bottom reports a move up: ignore it */
      if (y + window.innerHeight >= document.documentElement.scrollHeight - 2) return;
      if (!dy) return;
      if ((dy > 0) !== (run > 0)) run = 0;
      run += dy;
      if (run > 24) setBars(true);
      else if (run < -10) setBars(false);
    }
    window.addEventListener('scroll', function () { if (!raf) raf = requestAnimationFrame(onScroll); }, { passive: true });
    window.addEventListener('resize', function () { if (window.innerWidth >= 1025) setBars(false); });
    bars.addEventListener('focusin', function () { run = 0; setBars(false); });
    window.addEventListener('pageshow', function () { lastY = Math.max(0, window.scrollY || 0); run = 0; });
    root._erxShowBars = function () { run = 0; setBars(false); };
    root._erxSyncScroll = function () { lastY = Math.max(0, window.scrollY || window.pageYOffset || 0); run = 0; };
  }

  function init(root) {
    if (root.__erxInit) return;
    root.__erxInit = true;
    initDesktop(root);
    initPhone(root);
    root.querySelectorAll('[data-erx-cart]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        if (root._erxCloseMenu) root._erxCloseMenu();
        openCart(e);
      });
    });
  }

  function boot() { document.querySelectorAll('[data-erx-hd]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();

/* Omnisend's "30 € nuolaida" side tab (it renders its own div straight under <body>, outside the theme) had no way to be
   dismissed and took a strip of the phone screen. Find it by shape and text, give it an X, and keep it hidden for 7 days
   once closed. Matched by the discount word in LT / LV / EE, so it survives Omnisend restyling the tab. */
(function () {
  'use strict';
  if (window.__erxTeaserX) return;
  window.__erxTeaserX = true;
  var KEY = 'erx-teaser-closed', DAYS = 7;
  var WORD = /(nuolaida|atlaide|allahindlus|soodustus|rabat)/i;
  var closed = function () { try { var t = +localStorage.getItem(KEY); return t && Date.now() - t < DAYS * 864e5; } catch (e) { return false; } };
  var isTab = function (b) {
    if (!WORD.test(b.textContent || '')) return false;
    var r = b.getBoundingClientRect();
    return r.width > 0 && r.width < 90 && r.height > r.width * 1.5 && r.height < 300; // the upright side tab only, never the popup's own buttons
  };
  var rootOf = function (el) { while (el.parentElement && el.parentElement !== document.body) el = el.parentElement; return el; };
  /* the tab is drawn rotated, so place the X by the box as it is SEEN: its top-right corner on screen */
  var place = function (b, x) {
    var r = b.getBoundingClientRect(), h = x.parentElement.getBoundingClientRect();
    x.style.left = (r.right - h.left - 14) + 'px';
    x.style.top = (r.top - h.top - 10) + 'px';
  };
  var arm = function (b) {
    if (b.__erxX) return;
    var root = rootOf(b);
    if (root.matches('main, header, footer, script, #cart-drawer, [data-erx-hd]')) return;
    if (closed()) { root.style.setProperty('display', 'none', 'important'); return; }
    b.__erxX = true;
    var host = b.parentElement;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    var x = document.createElement('button');
    x.type = 'button'; x.className = 'erx-teaser-x'; x.setAttribute('aria-label', '×'); x.textContent = '×';
    x.style.cssText = 'position:absolute;z-index:2;width:24px;height:24px;padding:0;border:0;border-radius:50%;background:#1a1a1a;color:#fff;font:400 16px/24px system-ui,sans-serif;text-align:center;cursor:pointer;box-shadow:0 1px 4px rgba(0,0,0,.25);';
    host.appendChild(x); place(b, x);
    window.addEventListener('resize', function () { place(b, x); }, { passive: true });
    x.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation();
      try { localStorage.setItem(KEY, String(Date.now())); } catch (err) {}
      root.style.setProperty('display', 'none', 'important');
    });
  };
  var scan = function () {
    Array.prototype.forEach.call(document.body.children, function (c) {
      if (c.tagName !== 'DIV' || c.matches('#cart-drawer, #pandectes-banner, #pd-cp-preferences')) return;
      Array.prototype.forEach.call(c.querySelectorAll('button, a, [role="button"]'), function (b) { if (isTab(b)) arm(b); });
    });
  };
  var go = function () {
    scan();
    var t = 0;
    new MutationObserver(function () { clearTimeout(t); t = setTimeout(scan, 150); }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go); else go();
})();

/* Menu images: a failed one is retried once, then hidden, so a slow connection never shows the
   browser's broken-image icon in the menu tiles. */
document.addEventListener('error', function (e) {
  var img = e.target;
  if (!img || img.tagName !== 'IMG' || !img.closest('[data-erx-mm], [data-erx-panel]')) return;
  if (img.dataset.erxRetried) { img.style.visibility = 'hidden'; return; }
  img.dataset.erxRetried = '1';
  img.style.visibility = 'hidden';
  var src = img.currentSrc || img.src;
  setTimeout(function () {
    img.addEventListener('load', function () { img.style.visibility = ''; }, { once: true });
    img.removeAttribute('srcset');
    img.src = src;
  }, 1500);
}, true);
